import type { Attachment, Task } from "@engram/core";
import { Node, mergeAttributes } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import { Table, TableCell, TableHeader, TableView } from "@tiptap/extension-table";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from "@tiptap/react";
import { Calendar, Download, File, FileAudio, FileImage, FileText, Flag, MoreHorizontal, Pencil, Repeat } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { deleteTask, toggleTask, updateTask } from "../../db/actions";
import { focusTaskTitle, insertNewTask } from "./tasks";
import { db } from "../../db/client";
import { useRowById } from "../../db/rows";
import { editSketch } from "./sketch";
import { openTaskDialog } from "../TaskDialog";
import { useAttachmentUrl } from "../attachments";
import { shortDate } from "../format";

/** Links; note links carry data-note-link="<id>" and href="/note/<id>". */
export const EngramLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-note-link": { default: null },
    };
  },
}).configure({
  openOnClick: false,
  autolink: true,
  linkOnPaste: true,
  defaultProtocol: "https",
  HTMLAttributes: { rel: "noopener noreferrer nofollow", target: null },
  isAllowedUri: (url, ctx) => url.startsWith("/note/") || ctx.defaultValidate(url),
});

/**
 * TipTap's cell parser re-reads the table's whole <colgroup> for every cell, which made a 2,000-cell
 * table take ~120 ms just to parse. Same result, with the <col> widths read once per table.
 */
const colWidths = new WeakMap<Element, (string | null)[]>();
function parseColwidth(el: HTMLElement): number[] | null {
  const own = el.getAttribute("colwidth");
  if (own) return own.split(",").map((w) => parseInt(w, 10));
  const table = el.closest("table");
  const row = el.parentElement;
  if (!table || !row) return null;
  let cols = colWidths.get(table);
  if (!cols) colWidths.set(table, (cols = Array.from(table.querySelectorAll("colgroup > col"), (c) => c.getAttribute("width"))));
  const w = cols[Array.prototype.indexOf.call(row.children, el)];
  return w ? [parseInt(w, 10)] : null;
}
/** Evernote's cell menu: a background from a fixed palette (a key, so each theme can render it) and vertical alignment. */
export const CELL_BACKGROUNDS = ["gray-1", "yellow-1", "orange-1", "red-1", "purple-1", "blue-1", "green-1", "gray-2", "yellow-2", "orange-2", "red-2", "purple-2", "blue-2", "green-2"] as const;
const VALIGN = new Set(["top", "middle", "bottom"]);
const fastColwidth = <T extends typeof TableCell | typeof TableHeader>(ext: T) =>
  ext.extend({
    addAttributes() {
      const attrs = this.parent?.() ?? {};
      return {
        ...attrs,
        colwidth: { ...attrs.colwidth, parseHTML: parseColwidth },
        background: {
          default: null,
          parseHTML: (el: HTMLElement) => {
            const v = el.getAttribute("data-bg");
            return v && (CELL_BACKGROUNDS as readonly string[]).includes(v) ? v : null;
          },
          renderHTML: (a: { background?: string | null }) => (a.background ? { "data-bg": a.background } : {}),
        },
        valign: {
          default: null,
          parseHTML: (el: HTMLElement) => {
            const v = el.getAttribute("data-valign");
            return v && VALIGN.has(v) ? v : null;
          },
          renderHTML: (a: { valign?: string | null }) => (a.valign ? { "data-valign": a.valign } : {}),
        },
      };
    },
  });
export const FastTableCell = fastColwidth(TableCell);
export const FastTableHeader = fastColwidth(TableHeader);

/**
 * Tables carry a layout: null (note width) or "window" (Evernote's "Set table as window width": the table
 * spans the whole editor pane). TipTap's TableView doesn't copy node attributes to the DOM, so this one does.
 */
/** From this many cells, cell text off screen isn't laid out until it scrolls near (editor.css `.big-table`). */
const BIG_TABLE_CELLS = 400;
class EngramTableView extends TableView {
  constructor(node: PMNode, cellMinWidth: number, view: EditorView) {
    super(node, cellMinWidth, view);
    this.syncLayout(node);
  }
  override update(node: PMNode) {
    const ok = super.update(node);
    if (ok) this.syncLayout(node);
    return ok;
  }
  private syncLayout(node: PMNode) {
    if (node.attrs.layout) this.dom.setAttribute("data-layout", node.attrs.layout);
    else this.dom.removeAttribute("data-layout");
    let cells = 0;
    node.forEach((row) => (cells += row.childCount));
    this.dom.classList.toggle("big-table", cells > BIG_TABLE_CELLS);
  }
}
export const EngramTable = Table.extend({
  // Always our own view, from the editor's props. Otherwise the column-resize plugin supplies the table view once
  // plugins are attached, which changes ProseMirror's node-view set and rebuilds every note's DOM (props win over
  // plugins, so the plugin's view is simply not used).
  addNodeView() {
    return ({ node, view }) => new EngramTableView(node, this.options.cellMinWidth, view);
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      layout: {
        default: null,
        parseHTML: (el: HTMLElement) => (el.getAttribute("data-layout") === "window" ? "window" : null),
        renderHTML: (a: { layout?: string | null }) => (a.layout ? { "data-layout": a.layout } : {}),
      },
    };
  },
}).configure({ resizable: true, allowTableNodeSelection: true, View: EngramTableView as unknown as typeof TableView });

/** Image attachment (data-attachment) or external image (src). */
export const AttachmentImage = Node.create({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      attachment: { default: null, parseHTML: (el) => el.getAttribute("data-attachment"), renderHTML: (a) => (a.attachment ? { "data-attachment": a.attachment } : {}) },
      src: { default: null, parseHTML: (el) => (el.getAttribute("data-attachment") ? null : el.getAttribute("src")), renderHTML: (a) => (a.src ? { src: a.src } : {}) },
      alt: { default: "" },
      width: { default: null, parseHTML: (el) => el.getAttribute("width"), renderHTML: (a) => (a.width ? { width: a.width } : {}) },
      // A drawing made in Engram (Insert > Sketch): editable again.
      sketch: { default: false, parseHTML: (el) => el.hasAttribute("data-sketch"), renderHTML: (a) => (a.sketch ? { "data-sketch": "" } : {}) },
    };
  },
  parseHTML() {
    return [{ tag: "img[data-attachment]" }, { tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});

function ImageView({ node, updateAttributes, selected, editor, getPos }: ReactNodeViewProps) {
  const url = useAttachmentUrl(node.attrs.attachment);
  const src = node.attrs.attachment ? url : node.attrs.src;
  const ref = useRef<HTMLImageElement>(null);
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const img = ref.current;
    if (!img) return;
    const startX = e.clientX;
    const startW = img.getBoundingClientRect().width;
    const max = img.parentElement!.parentElement!.getBoundingClientRect().width;
    const move = (ev: PointerEvent) => {
      img.style.width = `${Math.max(40, Math.min(max, startW + ev.clientX - startX))}px`;
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      updateAttributes({ width: String(Math.round(img.getBoundingClientRect().width)) });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <NodeViewWrapper className={`image-node${selected ? " selected" : ""}`} data-drag-handle>
      <span className="image-frame">
        {src ? (
          <img ref={ref} src={src} alt={node.attrs.alt} style={node.attrs.width ? { width: `${node.attrs.width}px` } : undefined} draggable={false} />
        ) : (
          <span className="image-missing"><FileImage size={18} /> {node.attrs.alt || "Image"}</span>
        )}
        {editor.isEditable && src && <span className="resize-handle" onPointerDown={startResize} />}
        {editor.isEditable && node.attrs.sketch && (
          <button
            className="btn small sketch-edit"
            contentEditable={false}
            onMouseDown={(e) => e.preventDefault()}
            onClick={async () => {
              const row = await db.get<{ note_id: string }>("SELECT note_id FROM attachments WHERE id = ?", [node.attrs.attachment]);
              if (row) editSketch(editor, row.note_id, getPos, src);
            }}
          >
            <Pencil size={13} /> Edit sketch
          </button>
        )}
      </span>
    </NodeViewWrapper>
  );
}

/** Non-image attachment shown as a file chip. */
export const FileAttachment = Node.create({
  name: "fileAttachment",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      attachment: { default: null, parseHTML: (el) => el.getAttribute("data-attachment"), renderHTML: (a) => ({ "data-attachment": a.attachment }) },
      name: { default: "", parseHTML: (el) => el.textContent ?? "", renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: "div.attachment[data-attachment]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return ["div", mergeAttributes({ class: "attachment" }, HTMLAttributes), node.attrs.name];
  },
  addNodeView() {
    return ReactNodeViewRenderer(FileView);
  },
});

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Chrome and Edge render PDFs inline; the Android WebView doesn't (pdfViewerEnabled is false there).
const INLINE_PDF = typeof navigator !== "undefined" && navigator.pdfViewerEnabled === true;

function FileView({ node, selected }: ReactNodeViewProps) {
  const att = useRowById<Attachment>("attachments", node.attrs.attachment);
  const url = useAttachmentUrl(node.attrs.attachment);
  const mime = att?.mime ?? "";
  const pdf = mime === "application/pdf";
  // Evernote shows PDFs inline; the preview can be folded away per note view.
  const [preview, setPreview] = useState(true);
  const Icon = mime.startsWith("audio/") ? FileAudio : pdf ? FileText : File;
  const name = att?.filename || node.attrs.name || "Attachment";
  const showPdf = pdf && INLINE_PDF && preview && !!url;
  return (
    <NodeViewWrapper className={`file-node${selected ? " selected" : ""}${showPdf ? " with-preview" : ""}`} data-drag-handle>
      <span className="file-icon"><Icon size={20} /></span>
      <span className="file-meta">
        <span className="file-name">{name}</span>
        <span className="file-size">{att ? `${formatSize(att.size)} · ${mime.split("/")[1]?.toUpperCase() ?? ""}` : ""}</span>
      </span>
      {mime.startsWith("audio/") && url && <audio controls src={url} preload="none" />}
      {pdf && INLINE_PDF && url && (
        <button className="btn small" onClick={() => setPreview(!preview)}>
          {preview ? "Hide preview" : "Preview"}
        </button>
      )}
      {url && (
        <a className="icon-btn" href={url} download={name} title="Download" onClick={(e) => e.stopPropagation()}>
          <Download size={16} />
        </a>
      )}
      {url && pdf && (
        <a className="btn small" href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
          Open
        </a>
      )}
      {showPdf && <iframe className="pdf-preview" src={url} title={name} />}
    </NodeViewWrapper>
  );
}

/**
 * A task lives in the tasks table; the note body only holds its placeholder,
 * so widgets and the Tasks view can complete it without editing HTML.
 */
export const TaskRef = Node.create({
  name: "taskRef",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { id: { default: null, parseHTML: (el) => el.getAttribute("data-task-id"), renderHTML: (a) => ({ "data-task-id": a.id }) } };
  },
  parseHTML() {
    return [{ tag: "div[data-task-id]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TaskView);
  },
});

function TaskView({ node, selected, editor, getPos }: ReactNodeViewProps) {
  const task = useRowById<Task>("tasks", node.attrs.id);
  const [title, setTitle] = useState("");
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pending = useRef<string | null>(null);
  useEffect(() => {
    if (task && pending.current === null) setTitle(task.title);
  }, [task?.title]); // eslint-disable-line react-hooks/exhaustive-deps
  // Saved while typing (and on leaving), so closing the note right after typing loses nothing.
  const flushTitle = () => {
    clearTimeout(saveTimer.current);
    const t = pending.current;
    pending.current = null;
    if (t !== null && task && t !== task.title) void updateTask(task.id, { title: t });
  };
  useEffect(() => () => flushTitle(), [task?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Loading (a frame or two): an empty row of the loaded row's height, so the note doesn't jump as tasks arrive.
  if (task === undefined) return <NodeViewWrapper className="task-node loading" />;
  if (task === null || task.deleted) return <NodeViewWrapper className="task-node missing">Deleted task</NodeViewWrapper>;
  const done = task.completed_at != null;
  const overdue = !done && task.due_at != null && task.due_at < Date.now();

  // Evernote's task rows behave like a list: Enter adds the next task, Enter or Backspace on an
  // empty one leaves the list, arrows move between rows and the text around them.
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const pos = typeof getPos === "function" ? getPos() : undefined;
    if (pos == null || !editor.isEditable) return;
    const input = e.currentTarget;
    const end = pos + node.nodeSize;
    const $pos = editor.state.doc.resolve(pos);
    const before = $pos.nodeBefore;
    const after = editor.state.doc.resolve(end).nodeAfter;
    const empty = !input.value.trim();
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      flushTitle();
      if (empty) {
        editor.chain().insertContentAt({ from: pos, to: end }, { type: "paragraph" }).setTextSelection(pos + 1).focus().run();
        void deleteTask(task.id, { keepBody: true });
      } else void insertNewTask(editor, task.note_id, undefined, end);
    } else if (e.key === "Backspace" && empty && input.selectionStart === 0 && input.selectionEnd === 0) {
      e.preventDefault();
      void deleteTask(task.id, { keepBody: true });
      if (before?.type.name === "taskRef") {
        editor.chain().deleteRange({ from: pos, to: end }).run();
        focusTaskTitle(editor, before.attrs.id as string);
      } else editor.chain().insertContentAt({ from: pos, to: end }, { type: "paragraph" }).setTextSelection(pos + 1).focus().run();
    } else if (e.key === "ArrowUp" || (e.key === "ArrowLeft" && input.selectionStart === 0 && input.selectionEnd === 0)) {
      if (before?.type.name === "taskRef") focusTaskTitle(editor, before.attrs.id as string, e.key === "ArrowUp" ? "start" : "end");
      else if (before?.isTextblock) editor.chain().setTextSelection(pos - 1).focus().run();
      else return;
      e.preventDefault();
    } else if (e.key === "ArrowDown" || (e.key === "ArrowRight" && input.selectionStart === input.value.length)) {
      if (after?.type.name === "taskRef") focusTaskTitle(editor, after.attrs.id as string, "start");
      else if (after?.isTextblock) editor.chain().setTextSelection(end + 1).focus().run();
      else if (!after) editor.chain().insertContentAt(end, { type: "paragraph" }).setTextSelection(end + 1).focus().run();
      else return;
      e.preventDefault();
    }
  };
  return (
    <NodeViewWrapper className={`task-node${done ? " done" : ""}${selected ? " selected" : ""}`}>
      <button className={`task-check${done ? " checked" : ""}`} contentEditable={false} onClick={() => void toggleTask(task.id)} aria-label={done ? "Mark incomplete" : "Mark complete"} />
      <input
        className="task-title"
        data-task={task.id}
        value={title}
        readOnly={!editor.isEditable}
        placeholder="Task"
        onChange={(e) => {
          setTitle(e.target.value);
          pending.current = e.target.value;
          clearTimeout(saveTimer.current);
          saveTimer.current = setTimeout(flushTitle, 500);
        }}
        onBlur={flushTitle}
        onKeyDown={onKey}
      />
      {task.due_at != null && (
        <span className={`task-badge${overdue ? " overdue" : ""}`}>
          <Calendar size={12} /> {shortDate(task.due_at)}
        </span>
      )}
      {task.recurrence && <Repeat size={12} className="task-badge" />}
      {task.flagged ? <Flag size={13} className="task-flag" /> : null}
      <button
        className="icon-btn task-more"
        title="Task details"
        contentEditable={false}
        onClick={() => {
          flushTitle();
          openTaskDialog(task.id);
        }}
      >
        <MoreHorizontal size={15} />
      </button>
    </NodeViewWrapper>
  );
}
